/**
 * Calls to the MCP OAuth facade (mcp.biobam.com), which is a different service from JobService.
 *
 * Plain `fetch` rather than the Amplify `API` client on purpose: Amplify is configured for one
 * endpoint with its own auth header, and this is a separate origin whose consent endpoints want the
 * user's id token explicitly. Keeping it separate also makes it obvious in review that this talks to
 * the authorization server, not to the product API.
 */

import {Auth} from 'aws-amplify'

const FACADE_BASE = process.env.REACT_APP_MCP_OAUTH_BASE || 'https://mcp.biobam.com'

/** What the consent screen is allowed to show. Mirrors `ConsentDetails` in the facade. */
export interface ConsentDetails {
  requestId: string
  /**
   * The host of the client's metadata document — `claude.ai`, `chatgpt.com`, … This, and never a
   * name the client chose for itself, is what identifies the app to the user: the document is written
   * by the client, so its `client_name` is a claim, while the host is where it actually lives.
   */
  clientHost: string
  clientSource: 'cimd' | 'dcr' | 'static'
  scopes: string[]
  resource: string
  /** The code will be delivered to a loopback port on this machine (a desktop client). */
  loopbackRedirect: boolean
  email?: string
}

export interface ConsentDecision {
  redirectUrl: string
  connectionId?: string
}

export class ConsentError extends Error {}

async function idToken(): Promise<string> {
  const session = await Auth.currentSession()
  return session.getIdToken().getJwtToken()
}

async function parseOrThrow<T>(res: Response): Promise<T> {
  const text = await res.text()
  let body: any
  try {
    body = text ? JSON.parse(text) : {}
  } catch {
    throw new ConsentError('The authorization server returned an unexpected response.')
  }
  if (!res.ok) {
    throw new ConsentError(body?.error_description || body?.error || `Request failed (${res.status})`)
  }
  return body as T
}

/** What is being asked for. Readable without approving, so the page can render before any decision. */
export const getConsentDetails = async (requestId: string): Promise<ConsentDetails> => {
  const res = await fetch(`${FACADE_BASE}/consent/${encodeURIComponent(requestId)}`, {
    headers: {accept: 'application/json'},
  })
  return parseOrThrow<ConsentDetails>(res)
}

/**
 * Approve, binding the grant to a subscription.
 *
 * The id token is what proves this is the same person who signed in at the start of the flow — the
 * facade compares it against the identity it recorded then, so a request id on its own can never
 * authorize anything.
 */
export const approveConsent = async (
  requestId: string,
  subscriptionId: string,
  scopes?: string[]
): Promise<ConsentDecision> => {
  const res = await fetch(`${FACADE_BASE}/consent/${encodeURIComponent(requestId)}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
      authorization: `Bearer ${await idToken()}`,
    },
    body: JSON.stringify({approve: true, subscriptionId, ...(scopes ? {scopes} : {})}),
  })
  return parseOrThrow<ConsentDecision>(res)
}

/** Decline. The client is told, rather than left waiting on a redirect that never comes. */
export const denyConsent = async (requestId: string): Promise<ConsentDecision> => {
  const res = await fetch(`${FACADE_BASE}/consent/${encodeURIComponent(requestId)}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
      authorization: `Bearer ${await idToken()}`,
    },
    body: JSON.stringify({approve: false}),
  })
  return parseOrThrow<ConsentDecision>(res)
}

/** Human-readable description of each scope, for the consent list. */
export const SCOPE_LABELS: Record<string, {title: string; detail: string}> = {
  'mcp:read': {
    title: 'Browse tools and check run status',
    detail: 'See the OmicsBox tool catalogue, and the state and logs of runs it starts.',
  },
  'mcp:run': {
    title: 'Start and cancel analyses',
    detail: 'Submit tools to OmicsCloud and cancel them. Runs consume CloudUnits from the subscription below.',
  },
  'mcp:download': {
    title: 'Download results',
    detail: 'Fetch result files produced by runs it started.',
  },
}

/** One app's ongoing access, as shown in Settings → Connected apps. */
export interface Connection {
  connectionId: string
  clientHost: string
  clientSource: 'cimd' | 'dcr' | 'static'
  scopes: string[]
  subscriptionId: string
  resource: string
  createdAt: number
  lastUsedAt: number | null
  /**
   * What the client looked like when it last took a token.
   *
   * This is what tells sessions apart. A native client (Claude Code, Codex CLI, Claude Science)
   * redeems its code on the user's own machine over a loopback redirect, so every machine is its own
   * authorization; a hosted surface (claude.ai, Claude Desktop, ChatGPT) redeems server-side against
   * the user's account, so one grant covers every device they sign into.
   */
  lastUserAgent: string | null
  lastClientIp: string | null
}

/**
 * The apps this user has connected.
 *
 * Authenticated with the user's own session rather than a connector token: this is account
 * management, and a compromised connector must not be able to enumerate — let alone revoke — its
 * siblings.
 */
export const listConnections = async (): Promise<Connection[]> => {
  const res = await fetch(`${FACADE_BASE}/connections`, {
    headers: {accept: 'application/json', authorization: `Bearer ${await idToken()}`},
  })
  const body = await parseOrThrow<{connections: Connection[]}>(res)
  return body.connections ?? []
}

/** Disconnect an app: its refresh chain dies and every access token it holds is deleted. */
export const revokeConnection = async (connectionId: string): Promise<void> => {
  const res = await fetch(`${FACADE_BASE}/connections/${encodeURIComponent(connectionId)}`, {
    method: 'DELETE',
    headers: {accept: 'application/json', authorization: `Bearer ${await idToken()}`},
  })
  await parseOrThrow<unknown>(res)
}
